import React, { useState } from "react";
import { Share2, Copy, MessageCircle, Loader2, Ban, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { createReportShare, listReportShares, revokeReportShare } from "@/lib/api";
import { useAdStrings } from "@/lib/adStrings";

const shareUrl = (token) => `${window.location.origin}/r/${token}`;
const fmt = (d, l) => new Date(d).toLocaleDateString(l);

function ShareLinkBox({ link, S, l }) {
  const url = shareUrl(link.token);
  const wa = `https://wa.me/?text=${encodeURIComponent(S.waText(fmt(link.expires_at, l), url))}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(S.copied); } catch { toast.error(S.copyErr); }
  };
  return (
    <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3" data-testid="share-link-box">
      <input readOnly value={url} onFocus={(e) => e.target.select()} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] font-mono bg-white" data-testid="share-link-url" />
      <div className="mt-2 flex gap-2 flex-wrap">
        <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 text-xs font-semibold" data-testid="share-link-whatsapp"><MessageCircle className="w-3.5 h-3.5" /> {S.wa}</a>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold" data-testid="share-link-copy"><Copy className="w-3.5 h-3.5" /> {S.copy}</button>
      </div>
      <div className="text-[10px] text-slate-500 mt-1.5">{S.expiresOn} {fmt(link.expires_at, l)}. {S.saveNow}</div>
    </div>
  );
}

function ShareList({ items, onRevoke, busy, S, l }) {
  if (!items.length) return <div className="text-[11px] text-slate-400 italic" data-testid="share-links-empty">{S.none}</div>;
  return (
    <ul className="space-y-1.5" data-testid="share-links-list">
      {items.map((s) => (
        <li key={s.id} className="flex items-center justify-between gap-2 text-[11px] border border-slate-100 rounded-lg px-2 py-1.5" data-testid={`share-link-${s.id}`}>
          <span className="min-w-0 truncate">{S.createdOn} {fmt(s.created_at, l)} · {s.revoked ? S.revoked : s.active ? `${S.expires} ${fmt(s.expires_at, l)}` : S.expired} · {s.lang === "hi" ? "हिंदी" : "English"}</span>
          <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5 font-semibold flex-shrink-0" title={S.viewsTitle} data-testid={`share-views-${s.id}`}><Eye className="w-3 h-3" /> {s.views ?? 0}</span>
          {s.active && (
            <button type="button" onClick={() => onRevoke(s.id)} disabled={busy === s.id} className="inline-flex items-center gap-1 rounded-full bg-rose-50 hover:bg-rose-100 text-rose-700 px-2 py-0.5 font-semibold flex-shrink-0 disabled:opacity-50" data-testid={`share-revoke-${s.id}`}>
              <Ban className="w-3 h-3" /> {S.revoke}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function ShareReportPanel({ adId }) {
  const A = useAdStrings();
  const S = A.share;
  const l = A.locale;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => listReportShares(adId).then(setItems).catch(() => setItems([]));
  const openIt = () => { setOpen(true); setLink(null); load(); };
  const create = async () => {
    setBusy("create");
    try { setLink(await createReportShare(adId)); load(); toast.success(S.created); }
    catch { toast.error(S.createErr); }
    finally { setBusy(null); }
  };
  const revoke = async (id) => {
    setBusy(id);
    try { await revokeReportShare(id); if (link?.id === id) setLink(null); load(); toast.success(S.revokedOk); }
    catch { toast.error(S.revokeErr); }
    finally { setBusy(null); }
  };
  return (
    <>
      <button type="button" onClick={openIt} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-white hover:bg-amber-50 text-amber-800 px-2.5 py-1 text-[11px] font-semibold" data-testid={`ad-report-share-${adId}`}>
        <Share2 className="w-3 h-3" /> {S.btn}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] p-4 sm:p-6" data-testid="share-report-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{S.title}</DialogTitle>
            <DialogDescription className="text-xs">{S.desc}</DialogDescription>
          </DialogHeader>
          {link ? <ShareLinkBox link={link} S={S} l={l} /> : (
            <button type="button" onClick={create} disabled={busy === "create"} className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white py-2 text-sm font-semibold disabled:opacity-60" data-testid="share-create-btn">
              {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} {S.create}
            </button>
          )}
          <div className="text-[11px] font-semibold text-slate-600 mt-1">{S.links} <span className="font-normal text-slate-400">{S.viewsNote}</span></div>
          <ShareList items={items} onRevoke={revoke} busy={busy} S={S} l={l} />
        </DialogContent>
      </Dialog>
    </>
  );
}
